import { useCallback, useEffect, useState } from "react";
import { RootState } from "@/app/rootReducer";
import { fetchMyEmailNotificationsAction } from "@/features/myEmailNotifications/slices/myEmailNotificationSlice";
import { useDispatch, useSelector } from "react-redux";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

/**
 * Filter keys for email notifications.
 */
type MyEmailNotificationFilters = {
  searchTerm: string;
  approved: string; // "all" | "approved" | "unapproved"
  sent: string; // "all" | "sent" | "unsent"
};

export const useMyEmailNotifications = () => {
  const dispatch = useDispatch();

  const notifications = useSelector(
    (state: RootState) => state.myEmailNotifications.notifications,
  );
  const totalCount = useSelector(
    (state: RootState) => state.myEmailNotifications.totalCount,
  );
  const loading = useSelector(
    (state: RootState) => state.myEmailNotifications.loading,
  );
  const error = useSelector(
    (state: RootState) => state.myEmailNotifications.error,
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [sorting, setSorting] = useState<SortingState>([]);

  const [filters, setFilters] = useState<MyEmailNotificationFilters>({
    searchTerm: "",
    approved: "all",
    sent: "all",
  });

  const refetchNotifications = useCallback(() => {
    const sortBy = sorting.length ? sorting[0].id : undefined;
    const sortOrder = sorting.length
      ? sorting[0].desc
        ? "DESC"
        : "ASC"
      : undefined;

    let approvedVal: boolean | undefined;
    if (filters.approved === "approved") approvedVal = true;
    if (filters.approved === "unapproved") approvedVal = false;

    let sentVal: boolean | undefined;
    if (filters.sent === "sent") sentVal = true;
    if (filters.sent === "unsent") sentVal = false;

    dispatch(
      fetchMyEmailNotificationsAction({
        searchTerm: filters.searchTerm || undefined,
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        sortBy,
        sortOrder: sortOrder as "ASC" | "DESC" | undefined,
        approved: approvedVal,
        sent: sentVal,
      }),
    );
  }, [sorting, filters, pagination, dispatch]);

  useEffect(() => {
    refetchNotifications();
  }, [refetchNotifications]);

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue,
  ) => {
    setPagination((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleFilterChange = (filterKey: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
    // Reset page to 0 whenever filters change
    setPagination({
      pageIndex: 0,
      pageSize: pagination.pageSize,
    });
  };

  return {
    notifications,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
    refetchNotifications,
  };
};
